/**
 * Shared building blocks for every auction Overlay screen.
 *
 * Kept in one file so all overlays reuse:
 *   - `PHOTO_FALLBACK`       — placeholder image for missing player photos.
 *   - `useSaleAnnouncement`  — listens on the auction socket and briefly
 *                              flashes SOLD / UNSOLD data.
 *   - `SaleBanner`           — the visual for that announcement. Respects the
 *                              organizer's custom `sold_media_url` /
 *                              `unsold_media_url` (PNG / GIF / MP4 / 3GP).
 *   - `useCountdown`         — turns `state.timer_ends_at` into a ticking int.
 *   - `usePublicAuction`     — fetches `/public/auctions/{id}` and keeps it in
 *                              sync via websocket + 30 s polling safety-net.
 *   - `useOverlayState`      — fetches `/public/auctions/{id}/state`, keeps it
 *                              in sync via websocket + 20 s polling. Calls the
 *                              caller-supplied `extraReload` on any mutation so
 *                              overlays never sit on stale player/team data
 *                              after a sold / unsold / pick action.
 *   - `PlayerList`           — grid card list used by Team-Owner overlay tabs.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuctionSocket } from "@/lib/useAuctionSocket";
import PlayerPhoto from "@/components/PlayerPhoto";
import { resolveImageUrl } from "@/components/ImageInput";
import { CheckCircle, XCircle } from "@phosphor-icons/react";

export const PHOTO_FALLBACK = "/player-placeholder.svg";

// Iter24: label map + reader for player fields selected by the superadmin to
// render on the LED / broadcast overlays. Shared across every overlay so the
// same list is honored everywhere.
export const FIELD_LABELS = {
  total_matches: "MATCHES", total_runs: "RUNS", total_wickets: "WICKETS",
  hundreds: "100s", fifties: "50s", highest_score: "HIGHEST",
  best_bowling: "BEST", strike_rate: "SR", economy_rate: "ECON",
  batting_avg: "AVG", bowling_avg: "BOW AVG",
  mobile: "MOB", age: "AGE", role: "ROLE", location: "FROM",
  batting_style: "BAT", bowling_style: "BOWL", category: "CAT",
  father_name: "FATHER", dob: "DOB", whatsapp: "WA", email: "EMAIL",
  address: "ADDR", village: "VILLAGE", tehsil: "TEHSIL", district: "DIST",
  state: "STATE", pincode: "PIN", notes: "NAME OF CLUB",
};

export function readPlayerField(p, k) {
  if (!p) return null;
  if (p[k] != null && p[k] !== "") return p[k];
  return p.stats?.[k];
}

export function getLedDisplayFields(auction) {
  const list = auction?.registration_form_config?.led_display_fields || [];
  // player_name & photo_url always render explicitly on each overlay, so skip.
  return list.filter((k) => k !== "player_name" && k !== "photo_url");
}

// Iter24: derive a dark → mid → accent gradient from any hex accent color so
// every overlay's background matches the tournament theme. Uses HSL shifts —
// falls back to the default red/orange when no valid hex is provided.
function hexToRgb(hex) {
  if (!hex || typeof hex !== "string") return null;
  const h = hex.replace("#", "").trim();
  if (h.length !== 6 && h.length !== 3) return null;
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const num = parseInt(full, 16);
  if (Number.isNaN(num)) return null;
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}
function rgbToHex({ r, g, b }) {
  const to2 = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}
function shade({ r, g, b }, pct) {
  // pct < 0 → darker, pct > 0 → lighter
  const f = pct < 0 ? 0 : 255;
  const t = Math.abs(pct);
  return { r: r + (f - r) * t, g: g + (f - g) * t, b: b + (f - b) * t };
}
export function accentPalette(hex) {
  const rgb = hexToRgb(hex) || hexToRgb("#A50D14"); // fallback deep red
  return {
    accent: rgbToHex(rgb),
    mid: rgbToHex(shade(rgb, -0.35)),
    deep: rgbToHex(shade(rgb, -0.75)),
  };
}
export function useAccentStyle(auction) {
  const pal = accentPalette(auction?.accent_color);
  return {
    "--accent-color": pal.accent,
    "--accent-mid": pal.mid,
    "--accent-deep": pal.deep,
  };
}

// Iter27: single source of truth for the auction's brand logo. Superadmin
// enables `allow_custom_led_logo` on the auction, admin uploads `custom_led_logo_url`.
// When both are present we render the admin's logo everywhere inside the
// auction (overlays + Live Auction top bar). Falls back to the NitiSoft mark.
export function brandLogoFor(auction) {
  if (auction?.allow_custom_led_logo && auction?.custom_led_logo_url) {
    // resolveImageUrl handles data / http / /api / legacy raw paths
    return resolveImageUrl(auction.custom_led_logo_url);
  }
  return "/nitisoft-logo.png";
}

export function useSaleAnnouncement(auctionId, players, teams, durationMs = 5000) {
  const [announce, setAnnounce] = useState(null);
  useAuctionSocket(auctionId, useCallback((msg) => {
    if (!msg || !msg.type) return;
    if (msg.type === "player_sold" || msg.type === "player_unsold") {
      const player = players.find((p) => p.id === (msg.player_id || msg.player?.id)) || msg.player;
      const team = msg.type === "player_sold"
        ? (teams.find((t) => t.id === (msg.team_id || msg.team?.id)) || msg.team)
        : null;
      const amount = msg.amount || msg.sold_price || (player && player.sold_price);
      setAnnounce({
        kind: msg.type === "player_sold" ? "sold" : "unsold",
        player,
        team,
        amount,
        key: Date.now(),
      });
    }
  }, [players, teams]));
  useEffect(() => {
    if (!announce) return;
    const id = setTimeout(() => setAnnounce(null), durationMs);
    return () => clearTimeout(id);
  }, [announce, durationMs]);
  return announce;
}

const VIDEO_EXTS = [".3gp", ".3gpp", ".mp4", ".webm", ".mov"];
const isVideoUrl = (u) => !!u && VIDEO_EXTS.some((e) => u.split("?")[0].toLowerCase().endsWith(e));

export function SaleBanner({ announce, auction, position = "top" }) {
  if (!announce) return null;
  const isSold = announce.kind === "sold";
  const bg = isSold ? "from-emerald-600/95 to-emerald-800/95" : "from-amber-600/95 to-red-700/95";
  const customUrl = isSold ? auction?.settings?.sold_media_url : auction?.settings?.unsold_media_url;
  const posCls = position === "bottom"
    ? "fixed inset-x-0 bottom-4 z-40"
    : position === "center"
    ? "fixed inset-0 z-40 flex items-center justify-center"
    : "fixed inset-x-0 top-1/4 z-40 flex justify-center";
  if (customUrl) {
    const isVid = isVideoUrl(customUrl);
    return (
      <div key={announce.key} className={`${posCls} pointer-events-none`} data-testid={`sale-banner-${announce.kind}`}>
        <div className="flex flex-col items-center animate-in fade-in slide-in-from-bottom duration-500">
          {isVid ? (
            <video
              src={resolveImageUrl(customUrl)}
              className="max-h-[45vh] max-w-[80vw] object-contain drop-shadow-[0_10px_40px_rgba(0,0,0,0.6)]"
              autoPlay muted loop playsInline
            />
          ) : (
            <img
              src={resolveImageUrl(customUrl)}
              alt=""
              className="max-h-[45vh] max-w-[80vw] object-contain drop-shadow-[0_10px_40px_rgba(0,0,0,0.6)]"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}
          {announce.player && (
            <div
              className="text-2xl font-display uppercase text-white mt-2"
              style={{ textShadow: "0 3px 10px rgba(0,0,0,0.95), 0 0 4px rgba(0,0,0,0.9)" }}
            >{announce.player.name}{isSold && announce.amount != null ? ` — ${announce.amount} pts` : ""}</div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div key={announce.key} className={`${posCls} justify-center pointer-events-none`} data-testid={`sale-banner-${announce.kind}`}>
      <div className={`bg-gradient-to-br ${bg} border-4 border-white/20 shadow-2xl rounded-2xl px-10 py-8 max-w-xl w-[92%] text-center animate-in fade-in slide-in-from-top duration-500`}>
        <div className="flex items-center justify-center gap-3 mb-3">
          {isSold ? <CheckCircle weight="fill" size={44} className="text-white" /> : <XCircle weight="fill" size={44} className="text-white" />}
          <div className="font-display text-6xl uppercase leading-none text-white drop-shadow-lg">
            {isSold ? "SOLD" : "UNSOLD"}
          </div>
        </div>
        {announce.player && (
          <div className="text-2xl font-display uppercase text-white mt-2">{announce.player.name}</div>
        )}
        {isSold && announce.team && (
          <div className="flex items-center justify-center gap-3 mt-3">
            {announce.team.logo_url && (
              <img src={resolveImageUrl(announce.team.logo_url)} alt="" className="w-12 h-12 rounded object-cover border-2 border-white/40" onError={(e) => { e.target.style.display = "none"; }} />
            )}
            <div className="font-display text-3xl uppercase text-white leading-none">→ {announce.team.name}</div>
          </div>
        )}
        {isSold && announce.amount != null && (
          <div className="font-display text-5xl text-white mt-3 leading-none">{announce.amount}<span className="text-lg opacity-70 ml-1">pts</span></div>
        )}
      </div>
    </div>
  );
}

export function useCountdown(endsAt, status) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!endsAt || status !== "live") { setRemaining(0); return; }
    const tick = () => setRemaining(Math.max(0, Math.floor((new Date(endsAt).getTime() - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [endsAt, status]);
  return remaining;
}

export function usePublicAuction(auctionId) {
  const [auction, setAuction] = useState(null);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/auctions/${auctionId}`);
      setAuction(data.auction || null);
      setTeams(data.teams || []);
      setPlayers(data.players || []);
    } catch (e) { /* noop */ }
  }, [auctionId]);

  useEffect(() => { load(); }, [load]);

  // Safety-net polling — refresh every 30 s even if the WebSocket is stuck.
  useEffect(() => {
    if (!auctionId) return;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [auctionId, load]);

  useAuctionSocket(auctionId, useCallback((msg) => {
    if (!msg) return;
    if (
      msg.type === "__reconnected" ||
      msg.type === "player_sold" || msg.type === "player_unsold" ||
      msg.type === "player_added" || msg.type === "player_updated" ||
      msg.type === "player_deleted" || msg.type === "players_bulk_added" ||
      msg.type === "player_status_changed" ||
      msg.type === "auction_started" || msg.type === "auction_updated" ||
      msg.type === "auction_completed" || msg.type === "auction_reset" ||
      msg.type === "sale_cancelled" || msg.type === "cancel_sale" ||
      msg.type === "reauction_round" || msg.type === "reauction_single"
    ) {
      load();
    }
  }, [load]));

  return { auction, teams, players, reload: load };
}

export function useOverlayState(auctionId, extraReload) {
  const [state, setState] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/auctions/${auctionId}/state`);
      setState(data);
    } catch (e) { /* noop */ }
  }, [auctionId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!auctionId) return;
    // Iter33: tightened safety-net polling from 20s → 5s so overlays never
    // sit on stale points if a bid_placed frame is lost between reconnects.
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [auctionId, load]);

  useAuctionSocket(auctionId, useCallback((msg) => {
    if (!msg) return;
    if (msg.state) {
      setState(msg.state);
    }
    // Any bidding/timer/sold event → force a fresh state fetch too, so the
    // overlay never trails a bid.  Cheap: /state endpoint is small.
    if (msg.type === "bid_placed" || msg.type === "auction_started" ||
        msg.type === "player_sold" || msg.type === "player_unsold" ||
        msg.type === "sale_cancelled" || msg.type === "cancel_sale" ||
        msg.type === "auction_reset" || msg.type === "__reconnected") {
      load();
    }
    if (extraReload && msg.type && msg.type !== "__reconnected" && [
      "player_sold", "player_unsold", "sale_cancelled",
      "player_added", "player_updated", "player_deleted", "players_bulk_added",
      "player_status_changed", "reauction_round", "reauction_single",
      "auction_updated", "auction_completed", "auction_reset",
    ].includes(msg.type)) {
      extraReload();
    }
    if (msg.type === "__reconnected" && extraReload) {
      extraReload();
    }
  }, [load, extraReload]));

  return state;
}

export function PlayerList({ players, teamMap }) {
  if (players.length === 0) return <div className="text-zinc-500 text-center py-6 border border-dashed border-zinc-800 rounded">No players.</div>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
      {players.map((p) => {
        const team = p.sold_to ? teamMap[p.sold_to] : null;
        return (
          <div key={p.id} className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden relative" data-testid={`player-card-${p.id}`}>
            {/* Player-number badge — helps owners find a player quickly by ID */}
            {p.player_number != null && (
              <span
                className="absolute top-1.5 left-1.5 z-10 bg-amber-400 text-black font-black text-[11px] px-1.5 py-0.5 rounded shadow"
                data-testid={`player-num-${p.id}`}
              >#{p.player_number}</span>
            )}
            <PlayerPhoto src={p.photo_url} alt={p.name}>
              <span className="text-[10px] font-bold uppercase tracking-widest bg-black/60 backdrop-blur px-2 py-1 rounded border border-white/10 self-start ml-auto">{p.role}</span>
              <div>
                <div className="font-display text-base leading-tight uppercase drop-shadow-lg">{p.name}</div>
                <div className="label-mono text-zinc-300">{p.location || "—"}</div>
              </div>
            </PlayerPhoto>
            <div className="p-2 text-xs">
              <div className="flex justify-between">
                <span className="label-mono">Base</span>
                <span className="font-mono">{p.base_price}</span>
              </div>
              {team && (
                <div className="text-emerald-400 mt-1">→ {team.short_name}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
